import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import clsx from "clsx";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import pick from "lodash-es/pick";
import { useFormikContext } from "formik";

// Material
import Box from "@material-ui/core/Box";

// Config
import colors from "src/config/colors";
import useTranslate from "src/hooks/useTranslate";

// Components
import Button from "src/components/Button";
import Text from "src/components/Text";
import FormSectionHeader from "./FormSectionHeader";
import StatementInput from "./StatementInput";

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2, 3)
  },
  boxList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 1.5)
    }
  },
  boxButton: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2, 5, 5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 1.5, 3)
    }
  },
  boxButtonPaged: {
    justifyContent: "flex-start",
    gap: theme.spacing(1)
  },
  button: {
    width: "100%"
  },
  buttonPaged: {
    width: "190px"
  },
  buttonBack: {
    width: "190px",
    backgroundColor: colors.riverBed
  }
}));

const FormContent = ({
  onContinue,
  schema,
  isSai,
  pages,
  pageIndex,
  maxPageIndex,
  onNavigate
}) => {
  const getText = useTranslate();
  const classes = useStyles();
  const { validateForm, setTouched, errors } = useFormikContext();

  const isPaged = pages.length > 1;
  const isLastPage = pageIndex === pages.length - 1;
  const { title: pageTitle, fieldPaths } = pages[pageIndex];

  function errorsToTouched(errs) {
    if (!errs || typeof errs !== "object") return true;
    if (Array.isArray(errs)) return true;

    const touched = {};
    Object.keys(errs).forEach(key => {
      touched[key] = errorsToTouched(errs[key]);
    });
    return touched;
  }

  // Scoped to this page's fieldPaths (not the whole form): only what's
  // rendered right now can block leaving it. Blocks forward moves (Next,
  // Generate, a breadcrumb jump ahead) - required fields on pages not
  // reached yet get their own check when the user actually gets there.
  const validateCurrentPage = async () => {
    const errs = await validateForm();
    const pageErrors = pick(errs, fieldPaths);

    if (isEmpty(pageErrors)) return true;

    setTouched(errorsToTouched(pageErrors), true);

    const firstErrorPath = fieldPaths.find(path => get(errs, path));
    document
      .getElementById(firstErrorPath)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });

    return false;
  };

  // Backward moves are never blocked, but if the page being left is invalid,
  // onNavigate rolls maxPageIndex back to just before it - the breadcrumb
  // stops offering a shortcut past it until it's fixed and re-validated via
  // a forward move again, so a later page can never stay reachable while an
  // earlier one is broken.
  const isCurrentPageValid = async () => {
    const errs = await validateForm();
    return isEmpty(pick(errs, fieldPaths));
  };

  const handleContinue = async () => {
    if (!(await validateCurrentPage())) return;

    if (isLastPage) {
      onContinue();
    } else {
      onNavigate(pageIndex + 1);
    }
  };

  const handleBack = async () => {
    const leavingPageValid = await isCurrentPageValid();
    onNavigate(pageIndex - 1, { leavingPageValid });
  };

  const handleBreadcrumbNavigate = async targetIndex => {
    if (targetIndex > pageIndex) {
      if (!(await validateCurrentPage())) return;
      onNavigate(targetIndex);
      return;
    }

    const leavingPageValid = await isCurrentPageValid();
    onNavigate(targetIndex, { leavingPageValid });
  };

  let previousGroupTitle = null;

  return (
    <>
      <FormSectionHeader
        isSai={isSai}
        titleKey="statementPage.formTitle"
        pages={pages}
        pageIndex={pageIndex}
        maxPageIndex={maxPageIndex}
        onNavigate={handleBreadcrumbNavigate}
      />
      {isPaged && pageTitle && (
        <Box className={classes.sectionTitle}>
          <Text type="heading3">{pageTitle}</Text>
        </Box>
      )}
      <Box className={classes.boxList}>
        {fieldPaths.map(key => {
          const field = schema.properties[key];
          const showGroupTitle =
            field.groupTitle && field.groupTitle !== previousGroupTitle;
          previousGroupTitle = field.groupTitle;

          return (
            <React.Fragment key={key}>
              {showGroupTitle && (
                <Box className={classes.sectionTitle}>
                  <Text type="heading3">{field.groupTitle}</Text>
                </Box>
              )}
              <div id={key}>
                <StatementInput data={field} name={key} errors={errors[key]} />
              </div>
            </React.Fragment>
          );
        })}
      </Box>
      <Box
        className={clsx(classes.boxButton, isPaged && classes.boxButtonPaged)}
      >
        {isPaged && pageIndex > 0 && (
          <Button
            onClick={handleBack}
            className={classes.buttonBack}
            color="primary"
            variant="contained"
            disableElevation
          >
            {getText("common.valueLabels.back").toUpperCase()}
          </Button>
        )}
        <Button
          onClick={handleContinue}
          className={isPaged ? classes.buttonPaged : classes.button}
          color="primary"
          variant="contained"
          disableElevation
        >
          {isLastPage
            ? getText("statementPage.generateButton").toUpperCase()
            : getText("common.prompts.next").toUpperCase()}
        </Button>
      </Box>
    </>
  );
};

FormContent.propTypes = {
  isSai: PropTypes.bool.isRequired,
  onContinue: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  pageIndex: PropTypes.number.isRequired,
  maxPageIndex: PropTypes.number.isRequired,
  schema: PropTypes.shape({
    properties: PropTypes.shape({})
  }).isRequired,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      fieldPaths: PropTypes.arrayOf(PropTypes.string)
    })
  ).isRequired
};

export default FormContent;
